// CreateMemberSexualIdentity.jsx
import { useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";

import api from "@/utils/axios";
import i18n from "@/i18n";
import cls from "./CreateMemberSexualIdentity.module.scss";
import { SEXUAL_IDENTITIES } from "@/enums/lifestyleEnums";
import { useAuth } from "@/app/auth-context-state";

/* Welcome-style hero placeholders (q6) */
import mrImg from "@/assets/UI/memberCreate/q6Mr.webp";
import mrMImg from "@/assets/UI/memberCreate/q6MrM.webp";
import mrsImg from "@/assets/UI/memberCreate/q6Mrs.webp";
import mrsMImg from "@/assets/UI/memberCreate/q6MrsM.webp";

/* After-form cards (educational) */
import heterosexualImg from "@/assets/UI/memberCreate/q6heterosexual.webp";
import homosexualImg from "@/assets/UI/memberCreate/q6homosexual.webp";
import bisexualImg from "@/assets/UI/memberCreate/q6bisexual.webp";
import pansexualImg from "@/assets/UI/memberCreate/q6pansexual.webp";
import queerImg from "@/assets/UI/memberCreate/q6queer.webp";
import demisexualImg from "@/assets/UI/memberCreate/q6demisexual.webp";
import asexualImg from "@/assets/UI/memberCreate/q6asexual.webp";

/* ---- Framer animations (same feel as CreateMemberGender) ---- */
const floatSlow = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.55, 1, 0.55],
    scale: [1, 1.03, 1],
    transition: { duration: 10, ease: "easeInOut", repeat: Infinity },
  },
};

const floatSlowAlt = {
  hidden: { opacity: 0.55 },
  show: {
    opacity: [0.72, 1, 0.72],
    scale: [1.03, 1, 1.03],
    x: [0, -6, 0],
    transition: { duration: 12, ease: "easeInOut", repeat: Infinity },
  },
};

/* Outlet context hook (same pattern you already use in this flow) */
function useMemberLayoutContext() {
  return useOutletContext();
}

/* Emoji meta per identity */
const IDENTITY_META = {
  heterosexual: "💞",
  homosexual: "🌈",
  bisexual: "💖",
  pansexual: "✨",
  queer: "🔥",
  demisexual: "💫",
  asexual: "🤍",
  sapiosexual: "🧠",
  bicurious: "🧷",
  heteroflexible: "😉",
  homoflexible: "😈",
  self_describe: "📝",
  not_select: "🫧",
};

const CARD_KEYS = [
  "heterosexual",
  "homosexual",
  "bisexual",
  "pansexual",
  "queer",
  "demisexual",
  "asexual",
];

const CARD_IMAGES = {
  heterosexual: heterosexualImg,
  homosexual: homosexualImg,
  bisexual: bisexualImg,
  pansexual: pansexualImg,
  queer: queerImg,
  demisexual: demisexualImg,
  asexual: asexualImg,
};

/* Closing text key helper (keeps your current behavior) */
function getClosingKey({ isCouple, rawGender }) {
  if (isCouple) return "onboarding.sexual_identity.closing.couple";

  if (rawGender === "man" || rawGender === "trans_man") {
    return "onboarding.sexual_identity.closing.man";
  }
  if (rawGender === "woman" || rawGender === "trans_woman") {
    return "onboarding.sexual_identity.closing.woman";
  }
  return "onboarding.sexual_identity.closing.other";
}

export default function CreateMemberSexualIdentity() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();

  const layoutCtx = useMemberLayoutContext() || {};
  const { memberType, isCouple: isCoupleCtx, rawGender } = layoutCtx;
  const isCouple = Boolean(isCoupleCtx || memberType === "couple");

  const [submitting, setSubmitting] = useState(false);
  const [memberChoice, setMemberChoice] = useState("");
  const [partnerChoice, setPartnerChoice] = useState("");

  const MrD = useMemo(() => mrImg, []);
  const MrsD = useMemo(() => mrsImg, []);
  const MrM = useMemo(() => mrMImg, []);
  const MrsM = useMemo(() => mrsMImg, []);

  const t = (k, o) => i18n.t(k, o);

  const title = t("onboarding.sexual_identity.title", {
    defaultValue: "Sexual identity",
  });

  const subtitle = !isCouple
    ? t("onboarding.sexual_identity.single.question", {
        defaultValue: "Which sexual identity feels most like you?",
      })
    : t("onboarding.sexual_identity.couple.lead", {
        defaultValue: "Choose one for you, and one for your partner.",
      });

  const coupleDetail = isCouple
    ? t("onboarding.sexual_identity.couple.detail", {
        defaultValue: "This helps us match vibes and keep things respectful.",
      })
    : "";

  const partnerLabel = t("onboarding.sexual_identity.partner.label", {
    defaultValue: "Now pick your partner’s sexual identity",
  });

  const hintFallback =
    t("onboarding.sexual_identity.tapToSelect", {
      defaultValue: "Tap to select what feels right 💫",
    }) || "Tap to select";

  const identities =
    Array.isArray(SEXUAL_IDENTITIES) && SEXUAL_IDENTITIES.length
      ? SEXUAL_IDENTITIES
      : [
          "heterosexual",
          "homosexual",
          "bisexual",
          "pansexual",
          "queer",
          "demisexual",
          "asexual",
          "sapiosexual",
          "bicurious",
          "heteroflexible",
          "homoflexible",
          "self_describe",
          "not_select",
        ];

  const options = identities
    .filter((v) => v !== "not_select") // keep consistent with gender step
    .map((value) => {
      const labelKey = `onboarding.sexual_identity.options.${value}.label`;
      const hintKey = `onboarding.sexual_identity.options.${value}.hint`;

      return {
        value,
        emoji: IDENTITY_META[value] || "🌈",
        title: t(labelKey, { defaultValue: value }),
        hint: t(hintKey, { defaultValue: "" }) || hintFallback,
      };
    });

  const cards = CARD_KEYS.map((key) => ({
    key,
    img: CARD_IMAGES[key],
    emoji: IDENTITY_META[key] || "🌈",
    title: t(`onboarding.sexual_identity.cards.${key}.title`, {
      defaultValue: t(`onboarding.sexual_identity.options.${key}.label`, {
        defaultValue: key,
      }),
    }),
    desc: t(`onboarding.sexual_identity.cards.${key}.desc`, {
      defaultValue: "",
    }),
  }));

  const closingKey =
    memberChoice && (!isCouple || partnerChoice)
      ? getClosingKey({ isCouple, rawGender })
      : null;

  const closingText = closingKey ? t(closingKey, { defaultValue: "" }) : "";

  async function onSubmit(e) {
    e.preventDefault();

    if (!memberChoice) {
      toast.info(
        t("onboarding.sexual_identity.toast.member_required", {
          defaultValue: "Please pick one for you.",
        }),
      );
      return;
    }
    if (isCouple && !partnerChoice) {
      toast.info(
        t("onboarding.sexual_identity.toast.partner_required", {
          defaultValue: "Please pick one for your partner.",
        }),
      );
      return;
    }

    const payload = isCouple
      ? {
          memberSexualIdentity: memberChoice,
          partnerSexualIdentity: partnerChoice,
        }
      : { memberSexualIdentity: memberChoice };

    setSubmitting(true);
    try {
      const { data } = await api.post(
        "/v1/member-create/sexual_identity",
        payload,
        {
          skipToast: true,
        },
      );

      await refreshMe();
      navigate(data?.next || "/member/sexual_role", { replace: true });
    } catch (err) {
      console.error("update sexual identity failed:", err);
      toast.error(
        t("onboarding.sexual_identity.toast.save_error", {
          defaultValue: "Something went wrong.",
        }),
      );
    } finally {
      setSubmitting(false);
    }
  }

  const submitDisabled =
    submitting || !memberChoice || (isCouple && !partnerChoice);

  return (
    <main className={cls.page}>
      {/* Fixed background stage */}
      <section className={cls.heroStage} aria-hidden="true">
        <div className={cls.mobileOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrM}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsM}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.desktopOnly}>
          <Motion.img
            className={`${cls.heroImg} ${cls.z1}`}
            src={MrD}
            alt=""
            variants={floatSlow}
            initial="hidden"
            animate="show"
            draggable="false"
          />
          <Motion.img
            className={`${cls.heroImg} ${cls.z3}`}
            src={MrsD}
            alt=""
            variants={floatSlowAlt}
            initial="hidden"
            animate="show"
            draggable="false"
          />
        </div>

        <div className={cls.stageTint} />
      </section>

      {/* Single-page scroll content (no nested scroll) */}
      <div className={cls.pageInner}>
        <div className={cls.formCard}>
          <div className={cls.formTop}>
            <div className={cls.formMeta}>
              <div className={cls.formTitle}>{title}</div>
              <div className={cls.formSub}>{subtitle}</div>
              {isCouple && coupleDetail ? (
                <div className={cls.formSub} style={{ marginTop: 8 }}>
                  {coupleDetail}
                </div>
              ) : null}
            </div>
          </div>

          <form className={cls.form} onSubmit={onSubmit} noValidate>
            {/* Member options */}
            <fieldset className={cls.options}>
              <legend className="sr-only">{subtitle}</legend>

              {options.map((opt) => {
                const checked = memberChoice === opt.value;

                return (
                  <label
                    key={`m-${opt.value}`}
                    className={`${cls.option} ${
                      checked ? cls.optionActive : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="memberSexualIdentity"
                      value={opt.value}
                      checked={checked}
                      onChange={() => setMemberChoice(opt.value)}
                    />

                    <span className={cls.optionBody}>
                      <span className={cls.optionEmoji} aria-hidden="true">
                        {opt.emoji}
                      </span>

                      <span className={cls.optionText}>
                        <strong>{opt.title}</strong>
                        <small>{opt.hint}</small>
                      </span>

                      <span className={cls.optionDot} aria-hidden="true" />
                    </span>
                  </label>
                );
              })}
            </fieldset>

            {/* Partner options (couple only) */}
            {isCouple && (
              <div className={cls.partnerBlock}>
                <div className={cls.partnerTitle}>{partnerLabel}</div>

                <fieldset className={cls.options}>
                  <legend className="sr-only">{partnerLabel}</legend>

                  {options.map((opt) => {
                    const checked = partnerChoice === opt.value;

                    return (
                      <label
                        key={`p-${opt.value}`}
                        className={`${cls.option} ${
                          checked ? cls.optionActive : ""
                        }`}
                      >
                        <input
                          type="radio"
                          name="partnerSexualIdentity"
                          value={opt.value}
                          checked={checked}
                          onChange={() => setPartnerChoice(opt.value)}
                        />

                        <span className={cls.optionBody}>
                          <span className={cls.optionEmoji} aria-hidden="true">
                            {opt.emoji}
                          </span>

                          <span className={cls.optionText}>
                            <strong>{opt.title}</strong>
                            <small>{opt.hint}</small>
                          </span>

                          <span className={cls.optionDot} aria-hidden="true" />
                        </span>
                      </label>
                    );
                  })}
                </fieldset>
              </div>
            )}

            {closingText ? (
              <div className={cls.closingNote} aria-live="polite">
                {closingText.split("\n").map((line) => (
                  <p key={line}>{line}</p>
                ))}
              </div>
            ) : null}

            {/* Submit */}
            <div className={cls.actions}>
              <button
                type="submit"
                className={cls.submit}
                disabled={submitDisabled}
                aria-busy={submitting ? "true" : "false"}
              >
                {submitting
                  ? t("common.saving", { defaultValue: "Saving…" })
                  : t("common.continue", { defaultValue: "Continue" })}
              </button>
            </div>
          </form>

          {/* After-form cards */}
          <section
            className={cls.cardsSection}
            aria-label="Sexual identity guide cards"
          >
            <div className={cls.cardsHeader}>
              <div className={cls.cardsTitle}>
                {t("onboarding.sexual_identity.guide.title", {
                  defaultValue: "Quick guide (if you’re curious)",
                })}
              </div>
              <div className={cls.cardsSub}>
                {t("onboarding.sexual_identity.guide.body", {
                  defaultValue:
                    "Pick what feels right above. These are just simple explanations to make it easier.",
                })}
              </div>
            </div>

            <div className={cls.cardsGrid}>
              {cards.map((c) => (
                <article key={c.key} className={cls.gCard}>
                  <div className={cls.gCardMedia}>
                    <img
                      className={cls.gCardImg}
                      src={c.img}
                      alt=""
                      loading="lazy"
                      draggable="false"
                    />
                    <div className={cls.gCardTint} />
                    <div className={cls.gCardIcon} aria-hidden="true">
                      <span className={cls.cardEmoji}>{c.emoji}</span>
                    </div>
                  </div>

                  <div className={cls.gCardBody}>
                    <div className={cls.gCardTitle}>{c.title}</div>
                    <div className={cls.gCardDesc}>{c.desc}</div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </div>

        <div className={cls.bottomSpace} />
      </div>
    </main>
  );
}
